import { CaretDown, MagnifyingGlass } from '@phosphor-icons/react';
import { useState } from 'react';
import Input from '@/components/Input';
import Tooltip from '@/components/Tooltip';
import { twMerge } from 'tailwind-merge';
import find from 'lodash/find';
import Button from '@/components/Button';
import Popup from '@/components/Popup';
import highlightMatchedText from '@/features/reports/utils/highlightMatchedText';

export default function HeaderDropdown({
  selectedId,
  setSelectedId,
  items,
  showCount,
}) {
  const [searchValue, setSearchValue] = useState('');

  const filteredItems = items.filter(
    (item) =>
      typeof item?.name === 'string' &&
      item.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  return (
    <div className="flex items-center gap-2">
      <Popup
        triggerEl={({ open, setOpen, setAnchorEl }) => (
          <div className="flex items-center gap-2">
            <button
              className={twMerge(
                'text-base text-pv-text-primary-text font-medium leading-[26px] flex items-center justify-between gap-2 cursor-text',
                open && 'text-pv-neutral-grey-300',
                items.length > 1 && 'cursor-pointer'
              )}
              onClick={(e) => {
                if (items.length > 1) {
                  setOpen(open ? false : true);
                  setAnchorEl(e.currentTarget);
                }
              }}
            >
              {find(items, { id: selectedId })?.name}
              {items.length > 1 && (
                <CaretDown
                  size={16}
                  className={twMerge(
                    'text-pv-neutral-grey-600 rotate-0 transition-transform duration-300',
                    open && 'transform rotate-180 text-pv-neutral-grey-300',
                    items.length <= 1 && 'text-pv-neutral-grey-300'
                  )}
                  weight="fill"
                />
              )}
            </button>
            {showCount && (
              <div className="bg-brand-ai-100 text-[10px] leading-[16px] px-1.5 rounded-md w-fit text-white">
                {items.length}
              </div>
            )}
          </div>
        )}
        popperProps={{
          className: 'z-20',
        }}
      >
        {() => (
          <div className="pt-3 h-full w-[374px] bg-white rounded-lg shadow-[0px_12px_12px_8px_rgba(122,122,122,0.08)] overflow-hidden">
            <Input
              type="text"
              placeholder="Search definitions"
              showClearInput
              value={searchValue}
              className={{
                wrapper: 'mx-3 mb-1.5',
                input: {
                  wrapper: 'relative p-2 max-h-[35px]',
                  root: 'peer ml-2 order-2 flex-1',
                  clear: 'order-3',
                },
              }}
              rightElem={
                <MagnifyingGlass
                  size={16}
                  className="text-pv-text-secondary-text peer-focus:text-pv-neutral-grey-600 order-1"
                />
              }
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
            />
            {searchValue && (
              <div className="px-4 text-pv-text-secondary-text text-xs leading-[19px]">
                {filteredItems.length === 0
                  ? 'No results for'
                  : 'Search results for'}{' '}
                <span className="text-pv-text-primary-text font-medium">{`“${searchValue}”`}</span>
              </div>
            )}
            {searchValue && filteredItems.length === 0 && (
              <Button
                variant="secondary"
                className={{
                  button: {
                    root: 'my-2 mx-4 px-3',
                  },
                }}
                onClick={() => setSearchValue('')}
              >
                Clear Search
              </Button>
            )}
            <div className="max-h-[132px] overflow-y-auto">
              {filteredItems.map((item) => (
                <div
                  className={twMerge(
                    'h-[44px] py-3 px-4 text-brand-ai-200 leading-[22px] text-sm cursor-pointer border-l-[3px] border-l-transparent hover:bg-pv-primary-primary-50 truncate',
                    selectedId === item.id &&
                      'border-l-brand-ai-100 font-medium'
                  )}
                  onClick={() => setSelectedId(item.id)}
                >
                  <Tooltip
                    title={item.name}
                    placement="top"
                    displayTooltipOnOverflow
                  >
                    <div
                      className="truncate"
                      dangerouslySetInnerHTML={{
                        __html: highlightMatchedText(item.name, searchValue),
                      }}
                    />
                  </Tooltip>
                </div>
              ))}
            </div>
          </div>
        )}
      </Popup>
    </div>
  );
}
